import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SecurityIcon from '@mui/icons-material/Security';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface PrivateChatTutorialProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const PrivateChatTutorial: React.FC<PrivateChatTutorialProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    // Step 0: Welcome
    {
      title: 'Welcome to Private Chat',
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            }}
          >
            <ChatIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>
          <Typography sx={{ fontSize: '16px', color: '#535862', lineHeight: 1.7, maxWidth: 450, mx: 'auto' }}>
            Private Chat is your <strong>secure AI assistant</strong> powered by your AI Gateway. 
            Have intelligent conversations with full privacy and control.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <SecurityIcon sx={{ color: '#667eea' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Secure</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <SmartToyIcon sx={{ color: '#A855F7' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Multi-Model</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                <BarChartIcon sx={{ color: '#10B981' }} />
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Trackable</Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 1: How it works
    {
      title: 'How It Works',
      content: (
        <Box>
          <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, mb: 3 }}>
            Private Chat uses <strong>your Flashgate resources</strong> to power conversations:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FolderIcon sx={{ color: '#617BFF', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>Select an Environment</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Choose your workspace and repository to contextualize your conversations.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SmartToyIcon sx={{ color: '#A855F7', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>AI-Powered Responses</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Your configured AI providers (OpenAI, Anthropic, etc.) power the chat through Flashgate.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: '12px' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BarChartIcon sx={{ color: '#10B981', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#181D27', mb: 0.5 }}>Analytics Dashboard</Typography>
                <Typography sx={{ fontSize: '13px', color: '#535862', lineHeight: 1.5 }}>
                  Track token usage, API calls, and policy compliance in real-time.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    // Step 2: Features
    {
      title: 'Key Features',
      content: (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(97, 123, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChartIcon sx={{ fontSize: 50, color: '#617BFF' }} />
            </Box>
          </Box>
          <Box sx={{ bgcolor: '#F0F2FF', borderRadius: '12px', p: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#617BFF', mb: 2 }}>
              What you can do
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                'Chat with multiple AI models through a unified interface',
                'Track all conversations with full search capabilities',
                'Monitor token usage and API costs per conversation',
                'Apply security policies to filter sensitive content',
                'Export conversation history for compliance',
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#617BFF' }} />
                  <Typography sx={{ fontSize: '13px', color: '#374151' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: '560px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Progress bar */}
        <Box sx={{ display: 'flex', gap: 0.5, p: 2, pb: 0 }}>
          {steps.map((_, idx: number) => (
            <Box
              key={idx}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: idx <= currentStep ? '#617BFF' : '#E5E7EB',
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, pt: 3 }}>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#181D27',
              mb: 3,
              textAlign: 'center',
            }}
          >
            {currentStepData.title}
          </Typography>

          {currentStepData.content}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            pt: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {currentStep > 0 ? (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                borderColor: '#D5D7DA',
                color: '#535862',
                borderRadius: '10px',
                fontWeight: 600,
                '&:hover': { borderColor: '#617BFF' },
              }}
            >
              Back
            </Button>
          ) : (
            <Button
              variant="text"
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                color: '#717680',
                fontSize: '13px',
                '&:hover': { color: '#535862' },
              }}
            >
              Skip
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={!isLastStep && <ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              bgcolor: '#617BFF',
              fontWeight: 600,
              px: 4,
              boxShadow: '0 4px 12px rgba(97, 123, 255, 0.3)',
              '&:hover': {
                bgcolor: '#5A6FE8',
              },
            }}
          >
            {isLastStep ? 'Start Chatting' : 'Next'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PrivateChatTutorial;
